import { Box, Group, Text, TextInput } from '@mantine/core';
import { DatePickerInput, DateTimePicker } from '@mantine/dates';
import { IconCalendar, IconTemplate } from '@tabler/icons-react';

// Check if value is a template placeholder (e.g., {{todayStart}}, {{daysFromNowEnd 5}})
const isTemplatePlaceholder = (value) => {
  return typeof value === 'string' && value.startsWith('{{') && value.endsWith('}}');
};

const DatePickerControl = ({
  value,
  onChange,
  size = 'sm',
  prepend,
  append,
  placeholder,
  className = '',
  style = {},
  children,
  showTimeSelect,
  disabled,
  ...props
}) => {
  // Check if value is a template placeholder
  const isTemplate = isTemplatePlaceholder(value);

  // Parse value - expect ISO string or Date
  let selected = null;
  if (value && !isTemplate) {
    const parsed = Date.parse(value);
    if (!isNaN(parsed)) {
      selected = new Date(value);
    }
  }

  const handleChange = (date) => {
    // Convert to ISO-8601 string in UTC for external consumers
    if (!date) {
      onChange(null);
      return;
    }
    // Mantine 8 returns strings in 'YYYY-MM-DD HH:mm:ss' format (local time)
    // Parse as local time and convert to UTC ISO string
    if (typeof date === 'string') {
      // Replace space with 'T' to create parseable format (interpreted as local time)
      const dateObj = new Date(date.replace(' ', 'T'));
      onChange(dateObj.toISOString());
    } else if (date instanceof Date) {
      onChange(date.toISOString());
    } else {
      onChange(null);
    }
  };

  const handleTemplateChange = (event) => {
    onChange(event.target.value || null);
  };

  const handleTemplateClear = () => {
    onChange(null);
  };

  const PickerComponent = showTimeSelect ? DateTimePicker : DatePickerInput;

  // If value is a template placeholder, show a TextInput instead
  const picker = isTemplate ? (
    <TextInput
      size={size}
      value={value}
      onChange={handleTemplateChange}
      placeholder={placeholder}
      disabled={disabled}
      leftSection={<IconTemplate size={16} />}
      rightSection={
        !disabled && (
          <Box
            component="button"
            type="button"
            onClick={handleTemplateClear}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              color: 'var(--mantine-color-dimmed)',
            }}
          >
            ×
          </Box>
        )
      }
      styles={{
        input: {
          fontFamily: 'monospace',
          fontSize: '0.85em',
        },
      }}
    />
  ) : (
    <PickerComponent
      size={size}
      value={selected}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      leftSection={<IconCalendar size={16} />}
      clearable
      {...props}
    />
  );

  // If we have prepend or append, wrap in a Group
  if (prepend || append) {
    return (
      <Box className={className} style={style}>
        <Group gap="xs" wrap="nowrap">
          {prepend && (
            <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {prepend}
            </Text>
          )}
          <Box style={{ flex: 1 }}>{picker}</Box>
          {append && (
            <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {append}
            </Text>
          )}
        </Group>
        {children && <Box mt="xs">{children}</Box>}
      </Box>
    );
  }

  return (
    <Box className={className} style={style}>
      {picker}
      {children && <Box mt="xs">{children}</Box>}
    </Box>
  );
};

export default DatePickerControl;
